<template>
  <transition name="fade" mode="out-in">
    <div
      class="container"
      v-if="state.ready && campaignStore.currentCampaign"
      :key="campaignStore.currentCampaign.campaign_id"
    >
      <template v-if="!state.onlyResults">
        <Header
          v-if="!state.fullScreen && spaces.length > 0"
          :name="campaignStore.currentCampaign.name"
          :isPublic="campaignStore.currentCampaign.invitation_id"
          :shareLink="campaignStore.campaignShareLink"
          :buttons="[
            {
              class: 'outline secondary icon icon-left',
              label: 'Paramètres de la campagne',
              icon: 'bi-gear',
              disabled: campaignStore.currentCampaign.archived,
              click: () => {
                state.showParameters = true;
              },
            },
            {
              class: 'primary icon icon-left',
              label: 'Ajouter des talents',
              step: '7',
              icon: 'bi-plus-lg',
              disabled: campaignStore.currentCampaign.archived,
              routeParams: {
                name: 'addCandidates',
                params: {
                  campaignId:
                    route.params.campaignId ||
                    '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
                },
              },
            },
          ]"
        />
        <div
          class="cards-container"
          v-if="!state.fullScreen && campaignStore.currentCampaign?.stats"
        >
          <StatsCard
            v-for="(stat, index) in campaignStore.currentCampaign?.stats"
            :key="index"
            v-bind="stat"
          />
        </div>
        <Toast
          v-if="campaignStore.currentCampaign.archived"
          text="Cette campagne est archivée, vous pouvez la
        restaurer à tout moment depuis votre page organisation."
          color="red"
          icon="bi-trash"
          style="max-width: unset"
          :closable="false"
        />
        <div class="filters-card card">
          <div class="tabs">
            <div
              class="tab"
              :class="{ current: currentCampaignTab === 'Finished' }"
              @click="appStore.changeTab('Finished')"
              v-text="
                `Résultats (${participantStore.finishedParticipants.length})`
              "
            />
            <div
              class="tab"
              :class="{
                current: currentCampaignTab === 'Activated',
                hasNews:
                  appStore.activatedTableHasNews[route.params.campaignId],
              }"
              @click="
                appStore.changeTab('Activated'),
                  (appStore.activatedTableHasNews[
                    route.params.campaignId
                  ] = false)
              "
              v-text="
                `Évaluations en cours (${
                  participantStore.campaignParticipants.filter(
                    (participant) =>
                      participant.activated === true && !participant.results
                  ).length
                })`
              "
            />
            <div
              class="tab"
              :class="{
                current: currentCampaignTab === 'ToActive',
                hasNews:
                  participantStore.campaignParticipants.filter(
                    (participant) => participant.activated === false
                  ).length > 0,
              }"
              @click="appStore.changeTab('ToActive')"
              v-text="
                `Évaluations à activer (${
                  participantStore.campaignParticipants.filter(
                    (participant) => participant.activated === false
                  ).length
                })`
              "
            />
          </div>
          <div class="filters">
            <VField
              class="light"
              placeholder="Recherche"
              name="search"
              icon="bi-search"
              v-model="state.search.value"
              @input="(event) => (state.search.value = event.target.value)"
            />
            <Button
              class="outline icon square"
              icon="bi-arrow-clockwise"
              v-tooltip="'Rafraîchir la liste des participants'"
              @click="refreshParticipantList()"
            />
            <Button
              class="outline icon square expend-btn"
              :icon="
                state.fullScreen
                  ? 'bi-arrows-angle-contract'
                  : 'bi-arrows-angle-expand'
              "
              v-tooltip="
                state.fullScreen
                  ? 'Affichage classique'
                  : 'Affichage plein écran'
              "
              @click="toggleFullScreen"
            />
          </div>
        </div>
      </template>
      <transition mode="out-in" name="fade">
        <component
          :is="state.tabs[currentCampaignTab]"
          :search="state.search"
          @toggleFullscreen="state.onlyResults = !state.onlyResults"
        />
      </transition>
      <ParametersModal
        :isOpen="state.showParameters"
        :currentTab="state.paramsTab"
        :campaign="campaignStore.currentCampaign"
        @close="state.showParameters = !state.showParameters"
      />
    </div>
  </transition>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { reactive, onBeforeMount, onMounted, inject } from "vue";
import { useParticipantStore } from "@/stores/participant.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useSpaceStore } from "@/stores/space.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useAppStore } from "@/stores/app.js";
import { useRoute } from "vue-router";
import Activated from "./partials/tables/ActivatedTable.vue";
import Finished from "./partials/tables/FinishedTable.vue";
import ToActive from "./partials/tables/ToActiveTable.vue";
import ParametersModal from "@/components/modals/app/parametersModal/ParametersModal.vue";
import Header from "@/components/Header.vue";

const emitter = inject("emitter");

const route = useRoute();

// Init stores
const spaceStore = useSpaceStore();
const { spaces } = storeToRefs(spaceStore);
const appStore = useAppStore();
const { currentCampaignTab } = storeToRefs(appStore);

const participantStore =
  route.name === "campaign" ? useParticipantStore() : useOnboardingStore();

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const state = reactive({
  fullScreen: false,
  onlyResults: false,
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  tabs: { Activated, ToActive, Finished },
  showParameters: appStore.showCampaignLinkInfo,
  paramsTab: appStore.campaignParametersTab,
  ready: false,
});

emitter.on("updateCalibration", () => {
  refreshParticipantList();
});

function toggleFullScreen() {
  state.fullScreen = !state.fullScreen;
}

async function refreshParticipantList() {
  await participantStore.getParticipants(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId
  );
  emitter.emit("refreshTableData");
  participantStore.computeResults();
}

onBeforeMount(async () => {
  if (route.name === "campaign") {
    if (!campaignStore.currentCampaign) {
      await campaignStore.getCampaign(
        route.params.companyId,
        route.params.spaceId,
        route.params.campaignId
      );
    }

    if (
      campaignStore.currentCampaign.calibration_id &&
      !campaignStore.campaignsCalibrations.find(
        (elt) =>
          elt.calibration_id === campaignStore.currentCampaign.calibration_id
      )
    ) {
      await campaignStore.getCalibration(
        campaignStore.currentCampaign.calibration_id
      );
    }

    if (!campaignStore.currentCampaign.participantsFetched) {
      await participantStore.getParticipants(
        route.params.companyId,
        route.params.spaceId,
        route.params.campaignId
      );
    }

    appStore.changeTab(route.query.tab || "Finished");
  }

  state.ready = true;
});

onMounted(async () => {
  if (!appStore.isOnboarded && appStore.onboardingPage != "campaign") {
    const campaignModule = await import("@/onboarding/campaign.js");

    setTimeout(() => {
      appStore.startChapter(campaignModule.default);
      appStore.onboardingPage = "campaign";
    }, 300);
  }
});
</script>
